import { CompetencyTracker } from '@/components/dashboard/CompetencyTracker'
import { OnboardingBanner } from '@/components/onboarding/OnboardingBanner'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useProgressData } from '@/hooks/useProgressData'
import type { PracticeTarget } from '@/hooks/useProgressData'
import { useStoryCount } from '@/hooks/useStoryCount'
import { useSubscription } from '@/hooks/useSubscription'
import { scoreBarColor, scoreColor } from '@/lib/scoreColors'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import type { Competency, Difficulty } from '@/types/interview'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

interface SessionRow {
  id: string
  target_role: string
  competency: string
  difficulty: 'entry' | 'mid' | 'senior'
  overall_score: number | null
  status: 'in_progress' | 'completed' | 'abandoned'
  question_count: number
  created_at: string
  completed_at: string | null
  is_demo: boolean
}

const COMPETENCY_LABELS: Record<string, string> = {
  leadership: 'Leadership',
  teamwork: 'Teamwork',
  communication: 'Communication',
  problem_solving: 'Problem Solving',
  time_management: 'Time Management',
  stakeholder_management: 'Stakeholder Management',
  attention_to_detail: 'Attention to Detail',
  mixed: 'Mixed',
}

const DIFFICULTY_LABELS: Record<string, string> = {
  entry: 'Entry',
  mid: 'Mid',
  senior: 'Senior',
}

const FREE_HISTORY_LIMIT = 3

interface CompetencyProgressRow {
  competency: string
  average_score: number
  session_count: number
}

function nextResetDate(windowStartsAt: string): string {
  const reset = new Date(new Date(windowStartsAt).getTime() + 30 * 24 * 60 * 60 * 1000)
  return reset.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

const STAR_ROWS = [
  { key: 'situation' as const, label: 'Situation', weight: '20%' },
  { key: 'task' as const, label: 'Task', weight: '15%' },
  { key: 'action' as const, label: 'Action', weight: '40%' },
  { key: 'result' as const, label: 'Result', weight: '25%' },
]

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user, signOut } = useAuthStore()
  const { profile, isPro, sessionsRemaining, loading: subLoading } = useSubscription()

  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [competencyProgress, setCompetencyProgress] = useState<CompetencyProgressRow[]>([])
  const [onboardingDismissed, setOnboardingDismissed] = useState<boolean>(
    () => localStorage.getItem('rr_onboarding_dismissed') === 'true'
  )

  function handleDismissOnboarding() {
    localStorage.setItem('rr_onboarding_dismissed', 'true')
    setOnboardingDismissed(true)
  }

  const storyCount = useStoryCount()
  const [intakeNudgeDismissed, setIntakeNudgeDismissed] = useState<boolean>(
    () => localStorage.getItem('rr_intake_nudge_dismissed') === 'true'
  )

  function handleDismissIntakeNudge() {
    localStorage.setItem('rr_intake_nudge_dismissed', 'true')
    setIntakeNudgeDismissed(true)
  }

  const {
    starAverages,
    practiceTargets,
    completedSessionCount,
    lastSessionRole,
    lastSessionDifficulty,
    loading: progressLoading,
  } = useProgressData()

  const { trackProgressSectionViewed } = useAnalytics()
  const progressTrackedRef = useRef(false)

  useEffect(() => {
    if (subLoading) return
    void fetchSessions(isPro)
    if (isPro) void fetchCompetencyProgress()
  }, [subLoading, isPro])

  // biome-ignore lint/correctness/useExhaustiveDependencies: trackProgressSectionViewed is a stable posthog wrapper
  useEffect(() => {
    if (progressLoading || completedSessionCount < 2 || progressTrackedRef.current) return
    progressTrackedRef.current = true
    trackProgressSectionViewed({
      completed_sessions: completedSessionCount,
      has_star_data: starAverages !== null,
    })
  }, [progressLoading, completedSessionCount, starAverages])

  async function fetchSessions(pro: boolean) {
    setSessionsLoading(true)
    const { data } = await supabase
      .from('sessions')
      .select(
        'id, target_role, competency, difficulty, overall_score, status, question_count, created_at, completed_at, is_demo'
      )
      .filter('drill_mode', 'eq', false)
      .eq('is_demo', false)
      .order('created_at', { ascending: false })
      .limit(pro ? 20 : FREE_HISTORY_LIMIT)

    setSessions(((data as SessionRow[]) ?? []).filter((s) => !s.is_demo))
    setSessionsLoading(false)
  }

  async function fetchCompetencyProgress() {
    const { data } = await supabase
      .from('competency_progress')
      .select('competency, average_score, session_count')
      .order('session_count', { ascending: false })

    setCompetencyProgress((data as CompetencyProgressRow[]) ?? [])
  }

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  const displayName = profile?.name ?? user?.email?.split('@')[0] ?? 'there'

  const completedSessions = sessions.filter((s) => s.status === 'completed')
  const scoredSessions = completedSessions.filter((s) => s.overall_score !== null)
  const avgScore =
    scoredSessions.length > 0
      ? Math.round(
          // biome-ignore lint/style/noNonNullAssertion: scoredSessions filtered to non-null above
          (scoredSessions.reduce((sum, s) => sum + s.overall_score!, 0) / scoredSessions.length) *
            10
        ) / 10
      : null

  const showProgress = !progressLoading && completedSessionCount >= 2 && starAverages !== null
  const showProgressNudge =
    !progressLoading && completedSessionCount > 0 && completedSessionCount < 2

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <span className="text-sm font-semibold tracking-tight">RoleRound</span>
        <div className="flex items-center gap-3">
          {!isPro && !subLoading && (
            <Link
              to="/settings"
              className="text-xs text-primary font-medium hover:opacity-80 transition-opacity"
            >
              Upgrade to Pro
            </Link>
          )}
          <Link
            to="/settings"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Settings
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Greeting */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Hey, {displayName}</h1>
          <p className="text-sm text-muted-foreground">
            {isPro
              ? 'Pro plan · unlimited sessions'
              : `${sessionsRemaining} free session${sessionsRemaining === 1 ? '' : 's'} remaining this month`}
          </p>
          {!isPro && profile?.sessionWindowStartsAt && (
            <p className="text-xs text-muted-foreground">
              Credits reset {nextResetDate(profile.sessionWindowStartsAt)}
            </p>
          )}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            value={isPro ? '∞' : String(sessionsRemaining)}
            label="Sessions left"
            loading={subLoading}
          />
          <StatCard
            value={avgScore !== null ? avgScore.toFixed(1) : '—'}
            label="Avg score"
            loading={sessionsLoading}
            valueColor={avgScore !== null ? scoreColor(avgScore) : undefined}
          />
          <StatCard
            value={String(completedSessions.length)}
            label="Completed"
            loading={sessionsLoading}
          />
        </div>

        {/* CTA */}
        <Link
          to="/interview/setup"
          className="flex items-center justify-between w-full rounded-xl bg-primary px-5 py-4 text-primary-foreground hover:opacity-90 transition-opacity"
        >
          <div>
            <p className="text-sm font-semibold">Start a new interview</p>
            <p className="text-xs opacity-80 mt-0.5">Pick a role, competency, and difficulty</p>
          </div>
          <span className="text-lg">→</span>
        </Link>

        {/* Onboarding banner — shown to new users with no sessions, until dismissed */}
        {!sessionsLoading && sessions.length === 0 && !onboardingDismissed && (
          <OnboardingBanner onDismiss={handleDismissOnboarding} />
        )}

        {/* STAR averages — shown to all users with ≥2 completed sessions */}
        {showProgress && (
          <section className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold">Your STAR averages</h2>
              <span className="text-xs text-muted-foreground">
                last {completedSessionCount} sessions
              </span>
            </div>
            <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
              {STAR_ROWS.map(({ key, label, weight }) => (
                <ScoreRow key={key} label={label} weight={weight} score={starAverages[key]} />
              ))}
            </div>
          </section>
        )}

        {/* What to Practice Next */}
        {showProgress && practiceTargets.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">What to practice next</h2>
            <div className="space-y-2">
              {practiceTargets.map((target) => (
                <PracticeTargetCard
                  key={target.key}
                  target={target}
                  role={lastSessionRole}
                  difficulty={lastSessionDifficulty}
                />
              ))}
            </div>
          </section>
        )}

        {/* Progress nudge for users with only 1 session */}
        {showProgressNudge && (
          <p className="text-center text-xs text-muted-foreground rounded-xl border border-dashed border-border px-4 py-3">
            Complete 1 more session to unlock your progress insights.
          </p>
        )}

        {/* Session history */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">Recent sessions</h2>

          {sessionsLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-16 rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          )}

          {!sessionsLoading && sessions.length === 0 && (
            <div className="rounded-xl border border-border bg-muted/30 px-5 py-8 text-center space-y-2">
              <p className="text-sm font-medium">No sessions yet</p>
              <p className="text-xs text-muted-foreground">
                Complete your first interview to see your history here.
              </p>
            </div>
          )}

          {!sessionsLoading && sessions.length > 0 && (
            <div className="space-y-2">
              {sessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          )}

          {!sessionsLoading && !isPro && sessions.length > 0 && (
            <Link
              to="/settings"
              className="flex items-center justify-between w-full rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:bg-muted/40 transition-colors"
            >
              <span>Full history available on Pro</span>
              <span className="text-xs font-medium text-primary">Upgrade →</span>
            </Link>
          )}
        </section>

        {/* Intake nudge — only while the bank is empty; disappears on first story */}
        {storyCount === 0 && !intakeNudgeDismissed && (
          <aside className="relative flex items-center gap-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <Link to="/story-intake?from=dashboard" className="flex-1 space-y-0.5">
              <p className="text-sm font-semibold">Build your story bank in 10 minutes</p>
              <p className="text-xs text-muted-foreground">
                A short chat turns your classes, clubs and jobs into interview-ready stories.
              </p>
            </Link>
            <button
              type="button"
              onClick={handleDismissIntakeNudge}
              aria-label="Dismiss"
              className="self-start text-sm leading-none text-muted-foreground transition-colors hover:text-foreground"
            >
              ✕
            </button>
          </aside>
        )}

        {/* Story Bank — 3 free stories, unlimited with Pro */}
        <section className="space-y-3">
          <Link
            to="/story-bank"
            className="flex items-center gap-4 rounded-xl border border-lime/40 bg-lime/10 px-4 py-3 hover:bg-lime/20 transition-colors"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-lime">
              <svg
                className="h-4 w-4 text-lime-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <div className="flex-1 space-y-0.5">
              <p className="text-sm font-semibold">Story Bank</p>
              <p className="text-xs text-muted-foreground">
                {isPro
                  ? 'Browse and manage your saved STAR stories.'
                  : 'Build your STAR story library — 3 stories free, unlimited with Pro.'}
              </p>
            </div>
            <span className="text-muted-foreground text-sm ml-2">→</span>
          </Link>
        </section>

        {/* Competency progress — Pro only */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">Competency progress</h2>

          {isPro ? (
            <CompetencyTracker
              scores={competencyProgress.map((r) => ({
                competency: r.competency as Competency,
                averageScore: r.average_score,
                sessionCount: r.session_count,
              }))}
            />
          ) : (
            <div className="relative rounded-xl border border-border overflow-hidden min-h-[160px]">
              {/* Blurred preview — rows stacked vertically to give the overlay enough height */}
              <div className="divide-y divide-border opacity-30 select-none pointer-events-none">
                {[
                  { label: 'Leadership', w: 'w-3/4', score: '3.8' },
                  { label: 'Communication', w: 'w-1/2', score: '3.1' },
                  { label: 'Problem Solving', w: 'w-2/3', score: '3.5' },
                ].map(({ label, w, score }) => (
                  <div key={label} className="flex items-center gap-4 px-4 py-3">
                    <div className="w-36 flex-shrink-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-[10px] text-muted-foreground">3 sessions</p>
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${w} rounded-full bg-emerald-500`} />
                    </div>
                    <span className="w-8 text-right text-sm font-semibold">{score}</span>
                  </div>
                ))}
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 text-center px-4">
                <p className="text-sm font-semibold">Competency trends — Pro feature</p>
                <p className="text-xs text-muted-foreground">
                  Track how your scores improve across sessions over time.
                </p>
                <Link
                  to="/settings"
                  className="mt-1 rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  Upgrade to Pro
                </Link>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatCardProps {
  value: string
  label: string
  loading?: boolean
  valueColor?: string
}

function StatCard({ value, label, loading, valueColor }: StatCardProps) {
  return (
    <div className="rounded-xl border border-border bg-background px-4 py-4 text-center space-y-1">
      {loading ? (
        <div className="h-7 w-12 rounded bg-muted animate-pulse mx-auto" />
      ) : (
        <p className={cn('text-2xl font-bold tabular-nums', valueColor ?? 'text-foreground')}>
          {value}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

interface ScoreRowProps {
  label: string
  weight: string
  score: number
}

function ScoreRow({ label, weight, score }: ScoreRowProps) {
  const pct = ((score - 1) / 4) * 100
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="w-28 flex-shrink-0">
        <span className="text-sm font-medium">{label}</span>
        <span className="ml-1.5 text-xs text-muted-foreground">({weight})</span>
      </div>
      <div
        role="progressbar"
        tabIndex={-1}
        aria-valuenow={score}
        aria-valuemin={1}
        aria-valuemax={5}
        aria-label={`${label} average score`}
        className="flex-1 h-2 rounded-full bg-muted overflow-hidden"
      >
        <div
          className={cn('h-full rounded-full transition-all', scoreBarColor(score))}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn('w-8 text-right text-sm font-semibold tabular-nums', scoreColor(score))}>
        {score.toFixed(1)}
      </span>
    </div>
  )
}

interface PracticeTargetCardProps {
  target: PracticeTarget
  role: string | null
  difficulty: Difficulty | null
}

function PracticeTargetCard({ target, role, difficulty }: PracticeTargetCardProps) {
  const { trackPracticeNextClicked } = useAnalytics()

  const params = new URLSearchParams()
  if (role) params.set('role', role)
  if (difficulty) params.set('difficulty', difficulty)
  params.set('type', target.type)
  params.set('key', target.key)
  params.set('score', target.score.toFixed(2))
  params.set('label', target.label)
  const href = `/drill?${params.toString()}`

  const title =
    target.type === 'star_component'
      ? `Strengthen your ${target.label}`
      : `Practice ${target.label}`

  const description =
    target.type === 'star_component'
      ? `Your ${target.label} averages ${target.score.toFixed(1)}/5 — focus here to lift your overall score`
      : `${target.label} is your lowest competency at ${target.score.toFixed(1)}/5`

  return (
    <div className="rounded-xl border border-border bg-background px-4 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Link
        to={href}
        onClick={() =>
          trackPracticeNextClicked({
            cta_type: target.type,
            target: target.key,
            role: role ?? '',
          })
        }
        className="flex-shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
      >
        Practice →
      </Link>
    </div>
  )
}

interface SessionCardProps {
  session: SessionRow
}

function SessionCard({ session }: SessionCardProps) {
  const competencyLabel = COMPETENCY_LABELS[session.competency] ?? session.competency
  const difficultyLabel = DIFFICULTY_LABELS[session.difficulty] ?? session.difficulty
  const date = new Date(session.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const isCompleted = session.status === 'completed'
  const isInProgress = session.status === 'in_progress'
  const isAbandoned = session.status === 'abandoned'

  const card = (
    <div
      className={cn(
        'rounded-xl border border-border bg-background px-4 py-3.5 flex items-center gap-4 transition-colors',
        isCompleted && 'hover:bg-muted/50 cursor-pointer',
        isInProgress && 'hover:bg-muted/50 cursor-pointer'
      )}
    >
      {/* Score or status badge */}
      <div className="flex-shrink-0 w-12 text-center">
        {isCompleted && session.overall_score !== null ? (
          <>
            <p
              className={cn(
                'text-lg font-bold tabular-nums leading-none',
                scoreColor(session.overall_score)
              )}
            >
              {session.overall_score.toFixed(1)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">/ 5.0</p>
          </>
        ) : (
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-[10px] font-medium',
              isInProgress
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {isInProgress ? 'Active' : isAbandoned ? 'Expired' : 'N/A'}
          </span>
        )}
      </div>

      {/* Session info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{session.target_role}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {competencyLabel} · {difficultyLabel} · {date}
        </p>
        {isInProgress && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Tap to continue</p>
        )}
      </div>

      {/* Arrow for navigable sessions */}
      {(isCompleted || isInProgress) && (
        <span className="text-muted-foreground text-sm flex-shrink-0">→</span>
      )}
    </div>
  )

  if (isCompleted) {
    return <Link to={`/feedback/${session.id}`}>{card}</Link>
  }

  if (isInProgress) {
    // The session page rehydrates itself from the server — no state to pass
    return <Link to={`/interview/session/${session.id}`}>{card}</Link>
  }

  return <div>{card}</div>
}
